import { ActionReducerMapBuilder, Draft, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/src/tsHelpers';

import { ModelError, State } from 'models';
import { SearchResultUpphovspersonSummary } from 'models/apiModels/SearchResultUpphovspersonSummary';
import { PagedQuery } from 'models/appModels/PagedQuery';

import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from './storeHelpers';

import BebyggelseApi from 'api/bebyggelseApi';

export const getUpphovspersoner = createAsyncThunk<
  SearchResultUpphovspersonSummary,
  PagedQuery,
  { rejectValue: ModelError }
>('upphovspersoner/fetch', async (pagedQuery: PagedQuery, { rejectWithValue }) => {
  const result = await BebyggelseApi.upphovsmanPersoner.get(pagedQuery);
  return errorOr<SearchResultUpphovspersonSummary>(result, rejectWithValue);
});

const initialState: State<SearchResultUpphovspersonSummary> = {
  data: { page: 0, results: [], page_size: 0, total_size: 0 },
  error: undefined,
  requestStatus: 'idle',
  requestType: undefined,
  currentRequestId: undefined
};

const upphovsmanPersonerSlice = createSlice({
  name: 'upphovspersoner',
  initialState,
  reducers: {
    clearUpphovspersoner: (state: Draft<State<SearchResultUpphovspersonSummary>>): void => {
      if (state.data) {
        state.data.results = [];
      }
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<State<SearchResultUpphovspersonSummary>>>) => {
    builder.addCase(
      getUpphovspersoner.pending,
      (state: Draft<State<SearchResultUpphovspersonSummary>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      getUpphovspersoner.fulfilled,
      (
        state: Draft<State<SearchResultUpphovspersonSummary>>,
        action: FulfilledActionType<SearchResultUpphovspersonSummary>
      ): void => {
        if (state.currentRequestId === action.meta.requestId) {
          if (action.payload.page > 0 && state.data) {
            const activeUpphovspersoner = action.payload.results.filter((upphovsperson) => upphovsperson.aktiv);
            action.payload.results = [...JSON.parse(JSON.stringify(state.data.results)), ...activeUpphovspersoner];
          }
          fulfilled(state, action);
        }
      }
    );
    builder.addCase(
      getUpphovspersoner.rejected,
      (state: Draft<State<SearchResultUpphovspersonSummary>>, action: RejectedActionType): void =>
        rejected(state, action)
    );
  }
});

export const { clearUpphovspersoner } = upphovsmanPersonerSlice.actions;

export default upphovsmanPersonerSlice.reducer;
